import {useCallback, useEffect} from 'react';

import NiceModal, {useModal} from '@ebay/nice-modal-react';
import {Observer} from 'mobx-react';

import type {
  PaymentExpandedCommon,
  RefundExpandedCommon,
} from '@yourcoach/shared/api/payment';

import {useI18n} from '@yourcoach/i18n';
import {alert} from '@yourcoach/shared/components/alert';
import {
  PaymentInfoContainer,
  PaymentInfoStore,
} from '@yourcoach/shared/modules/payments';
import {retryRefund} from '@yourcoach/shared/services/entity-action/refund/refund.retry';
import {handle3dSecure} from '@yourcoach/shared/utils/entity/card';
import {logger} from '@yourcoach/shared/utils/logger';
import {FlagManager, useMobxStore} from '@yourcoach/shared/utils/store';

import {ModalStickyFooter} from '@src/components/ModalNew';

import {MakeRefundModal} from '../make-refund-modal';
import * as S from './styles';

type Props = {
  payment?: PaymentExpandedCommon;
  paymentId?: string;
};

export const PaymentModal = NiceModal.create<Props>(({payment, paymentId}) => {
  const {t} = useI18n();
  const modal = useModal();

  useEffect(() => {
    logger.event('finances_payment_view');
  }, []);

  const store = useMobxStore(
    () =>
      new PaymentInfoStore({
        onFetchError: modal.hide,
        payment,
        paymentId,
      }),
  );
  const isOverlayVisible = useMobxStore(() => new FlagManager());

  const onRefundButtonClick = () => {
    NiceModal.show(MakeRefundModal, {
      payment: store.payment!.toJS,
    });
  };

  const onRetryRefundButtonClick = async (refund: RefundExpandedCommon) => {
    try {
      isOverlayVisible.setTrue();

      await retryRefund(refund, {
        handle3dSecure,
        returnUrl: window.location.href,
      });

      store.refresh();
    } catch (error) {
      logger.error(error);
      alert.error(error);
    } finally {
      isOverlayVisible.setFalse();
    }
  };

  const renderRefundButton = () => (
    <Observer>
      {() =>
        store.payment?.isRefundAvailable ? (
          <>
            <S.FooterButton onClick={onRefundButtonClick}>
              <S.FooterButtonText>{t('shared:refund')}</S.FooterButtonText>
            </S.FooterButton>
          </>
        ) : null
      }
    </Observer>
  );

  const onRequestClose = useCallback(() => {
    logger.event('finances_payment_view_close_tap');
    modal.hide();
  }, [modal]);

  return (
    <Observer>
      {() => (
        <S.Modal
          bodyClassName="body"
          customScroll
          isOpen={modal.visible}
          onAfterClose={modal.remove}
          onRequestClose={onRequestClose}
          showLoadingOverlay={isOverlayVisible.isTrue}
          title={t('shared:payment_label')}
        >
          <PaymentInfoContainer
            onRetryRefundButtonClick={onRetryRefundButtonClick}
            store={store}
          />
          <ModalStickyFooter position={20}>
            <S.Footer>{renderRefundButton()}</S.Footer>
          </ModalStickyFooter>
        </S.Modal>
      )}
    </Observer>
  );
});
